import { Type } from '@sinclair/typebox';
import { createPathGuidParameter } from '../../../routes/createPathParameter';
import { notFoundResponse, okResponse } from '../../../routes/defaultResponses';
import { makeNullable } from '../../../utils/typeboxHelpers';
import { surveyScopeExclusionTableSchema } from '../../surveyScopeExclusion/surveyScopeExclusionTableSchema';
import { surveyTableSchema } from '../survey/surveyTableSchema';

// Asset.type is stored as a string in the database; the controller validates
// the value against the AssetTypes enum on the server side. Keeping the schema
// as a bounded string mirrors `assetTableSchema.type`.
const assetTypeSchema = Type.String({ maxLength: 255 });

// Tree-shaped decomposition payload consumed by the iPhone-app's survey-detail
// screen. The mobile-app composes its three tabs (Details / Onderdelen /
// Opmerkingen) and its photo/video/voice/text capture targets straight from
// this structure, so any shape change here must be mirrored in
// `packages/mobile-app/src/api/surveys.ts`.

const manifestationNodeSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    unitId: Type.String({ format: 'uuid' }),
});

const unitNodeSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    elementId: Type.String({ format: 'uuid' }),
    manifestations: Type.Array(manifestationNodeSchema),
});

const elementNodeSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    // null means the element hangs directly off the object (no category).
    elementCategoryId: makeNullable(Type.String({ format: 'uuid' })),
    units: Type.Array(unitNodeSchema),
});

const elementCategoryNodeSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String(),
    elements: Type.Array(elementNodeSchema),
});

const decompositionSchema = Type.Object({
    elementCategories: Type.Array(elementCategoryNodeSchema),
    looseElements: Type.Array(elementNodeSchema),
});

const scopeResponseSchema = Type.Object({
    surveyId: Type.String({ format: 'uuid' }),
    definedScope: makeNullable(Type.String()),
    exclusions: Type.Array(surveyScopeExclusionTableSchema),
});

// Only the fields the iPhone-app actually reads — avoids sending heavy or
// internal columns (e.g. labels, photometric/UT fields, derived numbers) over
// the wire to a phone client.
const surveyForMobileSchema = Type.Pick(surveyTableSchema, [
    'id',
    'objectId',
    'description',
    'inspectionStandardType',
    'status',
    'statusUpdatedOn',
    'reportDeadlineOnDate',
    'assignmentUpdatedOn',
    'surveryedOn',
    'definedScope',
    'surveyRemarks',
    'assignedToUserId',
]);

const surveyForMobileResponseSchema = Type.Object({
    survey: surveyForMobileSchema,
    objectName: Type.String(),
    decomposition: decompositionSchema,
    scope: scopeResponseSchema,
});

// Target columns — exactly one of element/unit/manifestation must be set.
// The backend validates this; the schema keeps each optional to match the
// mobile-app's IAssetTarget shape.
const assetTargetFields = {
    objectId: Type.String({ format: 'uuid' }),
    elementId: Type.Optional(Type.String({ format: 'uuid' })),
    unitId: Type.Optional(Type.String({ format: 'uuid' })),
    manifestationId: Type.Optional(Type.String({ format: 'uuid' })),
};

const transcribeLanguageSchema = Type.Union([Type.Literal('nl-NL'), Type.Literal('en-US')]);

// Exported so the contract test in
// `__tests__/mobileBodyContracts.test.ts` can validate that the body shape
// the iPhone-app actually sends matches what the route handler will accept.
// Without these tests the surveyId-in-body bug shipped silently because
// TypeScript was happy on both sides while the runtime payload drifted.
export const presignRequestSchema = Type.Object({
    ...assetTargetFields,
    type: assetTypeSchema,
    fileName: Type.String({ maxLength: 255 }),
    contentType: Type.String({ maxLength: 255 }),
    fileSizeBytes: Type.Number(),
    transcribeLanguage: Type.Optional(transcribeLanguageSchema),
});

const presignResponseSchema = Type.Object({
    uploadUrl: Type.String(),
    storageKey: Type.String(),
    requiredHeaders: Type.Optional(Type.Record(Type.String(), Type.String())),
});

export const confirmRequestSchema = Type.Object({
    ...assetTargetFields,
    type: assetTypeSchema,
    storageKey: Type.String(),
    fileName: Type.String({ maxLength: 255 }),
    contentType: Type.String({ maxLength: 255 }),
    fileSizeBytes: Type.Number(),
    // Mobile-app passes the operator's language hint at presign AND confirm
    // because the queueTranscribeAudioJob call happens at confirm-time. The
    // presign request can't reach the confirm handler — there's no per-upload
    // state on the server between the two requests.
    transcribeLanguage: Type.Optional(transcribeLanguageSchema),
});

const confirmResponseSchema = Type.Object({
    assetId: Type.String({ format: 'uuid' }),
});

export const appendAdviceRequestSchema = Type.Object({
    ...assetTargetFields,
    text: Type.String(),
    source: Type.Union([Type.Literal('mobile-text'), Type.Literal('mobile-voice-transcript'), Type.Literal('mobile-video-transcript')]),
});

// One captured asset (photo / video / voice) attached to a decomposition target,
// as shown in the iPhone-app's per-row gallery. `viewUrl` is a ready-to-use
// (presigned, when on S3) GET URL so the app can render/play the file directly
// without a second authenticated round-trip.
const mobileAssetSummarySchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    type: assetTypeSchema,
    name: Type.String(),
    fileSizeBytes: Type.Number(),
    createdOn: Type.String(),
    durationSeconds: makeNullable(Type.Number()),
    viewUrl: Type.String(),
});

const mobileAssetListResponseSchema = Type.Object({
    assets: Type.Array(mobileAssetSummarySchema),
});

export const surveyMobilePaths = {
    '/api/surveys/{surveyId}/mobile': {
        get: {
            operationId: 'surveyMobileControllers.getMobile',
            parameters: [createPathGuidParameter('surveyId')],
            responses: {
                '200': {
                    description: 'Composed payload (survey + objectName + decomposition tree + scope) for the iPhone-app detail screen',
                    content: { 'application/json': { schema: surveyForMobileResponseSchema } },
                },
                ...notFoundResponse,
            },
        },
    },
    '/api/surveys/{surveyId}/mobile/assets': {
        get: {
            operationId: 'surveyMobileControllers.listMobileAssets',
            parameters: [
                createPathGuidParameter('surveyId'),
                { name: 'elementId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'unitId', in: 'query', schema: Type.String({ format: 'uuid' }) },
                { name: 'manifestationId', in: 'query', schema: Type.String({ format: 'uuid' }) },
            ],
            responses: {
                '200': {
                    description: 'Captured assets (photo/video/voice) attached to the given decomposition target',
                    content: { 'application/json': { schema: mobileAssetListResponseSchema } },
                },
                ...notFoundResponse,
            },
        },
    },
    '/api/surveys/{surveyId}/assets/presign': {
        post: {
            operationId: 'surveyMobileControllers.presignAsset',
            parameters: [createPathGuidParameter('surveyId')],
            requestBody: {
                content: { 'application/json': { schema: presignRequestSchema } },
                required: true,
            },
            responses: {
                '200': {
                    description: 'Presigned PUT URL + storage key',
                    content: { 'application/json': { schema: presignResponseSchema } },
                },
                ...notFoundResponse,
            },
        },
    },
    '/api/surveys/{surveyId}/assets/confirm': {
        post: {
            operationId: 'surveyMobileControllers.confirmAsset',
            parameters: [createPathGuidParameter('surveyId')],
            requestBody: {
                content: { 'application/json': { schema: confirmRequestSchema } },
                required: true,
            },
            responses: {
                '200': {
                    description: 'Created asset id',
                    content: { 'application/json': { schema: confirmResponseSchema } },
                },
                ...notFoundResponse,
            },
        },
    },
    '/api/surveys/{surveyId}/conditions/append-advice': {
        post: {
            operationId: 'surveyMobileControllers.appendAdvice',
            parameters: [createPathGuidParameter('surveyId')],
            requestBody: {
                content: { 'application/json': { schema: appendAdviceRequestSchema } },
                required: true,
            },
            responses: {
                ...okResponse,
                ...notFoundResponse,
            },
        },
    },
} as const;
